import React from "react";
import { ShieldCheck, Clock, BadgeCheck } from "lucide-react";
import { PhoneButton, PHONE } from "./PhoneButton";

const heroImage =
  "https://images.unsplash.com/photo-1737704295069-d2b7ababda36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxoYXBweSUyMHNlbmlvciUyMGNvdXBsZSUyMHNtaWxpbmd8ZW58MHx8fHwxNzgyNjg0NzUwfDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 bg-gradient-to-b from-[#fdf8ee] via-[#fafafa] to-white"
    >
      {/* soft texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, #0c4a6e 0px, transparent 1px), radial-gradient(circle at 80% 60%, #0c4a6e 0px, transparent 1px)",
          backgroundSize: "22px 22px, 28px 28px",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Left text column */}
        <div className="lg:col-span-7 fade-up">
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-4 py-2"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-600" />
            </span>
            <span className="text-sm md:text-base font-semibold text-blue-900 tracking-wide">
              Licensed Agents Available Now
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-slate-900"
          >
            Affordable Life Insurance{" "}
            <span className="text-blue-900">for Seniors</span> on a Fixed Income.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 text-xl md:text-2xl leading-relaxed text-slate-700 max-w-2xl"
          >
            One simple call. We shop{" "}
            <strong className="text-slate-900">30+ top-rated carriers</strong>{" "}
            to find your lowest rate. No medical exam required for most plans.
          </p>

          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
            <PhoneButton
              size="lg"
              testId="hero-call-button"
              label={`Call ${PHONE.display}`}
              showSubtext
            />
            <div className="text-base md:text-lg text-slate-700 leading-snug">
              <div className="font-semibold text-slate-900">
                Free Quote • No Obligation
              </div>
              <div className="text-slate-600">
                Mon–Sat • Speak to a real human, fast
              </div>
            </div>
          </div>

          {/* trust strip */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
            {[
              { Icon: ShieldCheck, text: "Licensed & Insured" },
              { Icon: Clock, text: "Coverage in Minutes" },
              { Icon: BadgeCheck, text: "30+ Carriers Shopped" },
            ].map(({ Icon, text }, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 px-4 py-3"
              >
                <Icon className="h-6 w-6 text-green-700" />
                <span className="text-base font-medium text-slate-800">
                  {text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right image column */}
        <div className="lg:col-span-5 fade-up">
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-amber-200/40 -z-10" />
            <img
              src={heroImage}
              alt="Happy senior couple feeling secure with their life insurance coverage"
              data-testid="hero-image"
              loading="eager"
              className="w-full h-[420px] md:h-[520px] object-cover rounded-[2rem] shadow-2xl shadow-slate-900/15 border border-white"
            />
            {/* floating quote card */}
            <div
              data-testid="hero-floating-card"
              className="hidden md:flex absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-slate-100 px-5 py-4 max-w-[270px] items-center gap-3"
            >
              <div className="h-12 w-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold text-lg">
                $
              </div>
              <div>
                <div className="text-sm text-slate-500 font-medium">
                  Plans starting at
                </div>
                <div className="text-2xl font-bold text-slate-900 font-serif-display leading-tight">
                  $9.95/mo
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
